import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import axios from 'axios';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
    Upload,
    message
} from 'antd';
import {
    CloudUploadOutlined,
    FileProtectOutlined,
    ReloadOutlined,
    SendOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text } = Typography;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const STATUS_COLORS = {
    draft: 'default',
    pending: 'processing',
    signed: 'success',
    rejected: 'error',
    expired: 'warning',
    error: 'error',
    cancelled: 'default'
};

const GoskeySignPage = () => {
    const [dashboard, setDashboard] = useState({
        stats: {},
        settings: {},
        recentLogs: []
    });
    const [documents, setDocuments] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createForm] = Form.useForm();
    const [settingsForm] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [filterStatus, setFilterStatus] = useState();

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [dashRes, docsRes, logsRes] = await Promise.all([
                axios.get('/api/goskey/dashboard', {
                    headers: authHeaders()
                }),
                axios.get('/api/goskey/documents', {
                    headers: authHeaders(),
                    params: { status: filterStatus || undefined }
                }),
                axios.get('/api/goskey/logs', {
                    headers: authHeaders(),
                    params: { limit: 200 }
                })
            ]);
            setDashboard(dashRes.data.data || {});
            setDocuments(docsRes.data.data || []);
            setLogs(logsRes.data.data || []);
            settingsForm.setFieldsValue(dashRes.data.data?.settings || {});
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка Госключ'
            );
        } finally {
            setLoading(false);
        }
    }, [filterStatus, settingsForm]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const createDocument = async values => {
        if (!fileList[0]?.originFileObj) {
            message.error('Прикрепите файл документа');
            return;
        }
        const form = new FormData();
        form.append('title', values.title);
        if (values.description) {
            form.append('description', values.description);
        }
        form.append('signer_fio', values.signer_fio || '');
        form.append('signer_phone', values.signer_phone || '');
        form.append('signer_snils', values.signer_snils || '');
        form.append('signer_email', values.signer_email || '');
        form.append('sign_type', values.sign_type || 'УНЭП');
        if (values.expires_at) {
            form.append(
                'expires_at',
                values.expires_at.toISOString()
            );
        }
        form.append('file', fileList[0].originFileObj);

        try {
            await axios.post('/api/goskey/documents', form, {
                headers: {
                    ...authHeaders(),
                    'Content-Type': 'multipart/form-data'
                }
            });
            message.success('Документ создан');
            createForm.resetFields();
            setFileList([]);
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const sendDocument = async id => {
        try {
            await axios.post(
                `/api/goskey/documents/${id}/send`,
                {},
                { headers: authHeaders() }
            );
            message.success('Документ отправлен в Госключ');
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const setStatus = async (id, status) => {
        try {
            await axios.post(
                `/api/goskey/documents/${id}/status`,
                { status },
                { headers: authHeaders() }
            );
            message.success(`Статус: ${status}`);
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const uploadSigned = async (id, file) => {
        const form = new FormData();
        form.append('signed', file);
        try {
            await axios.post(
                `/api/goskey/documents/${id}/signed`,
                form,
                {
                    headers: {
                        ...authHeaders(),
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            message.success('Подписанный файл сохранён');
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const saveSettings = async values => {
        try {
            await axios.put('/api/goskey/settings', values, {
                headers: authHeaders()
            });
            message.success('Настройки сохранены');
            loadAll();
        } catch (error) {
            message.error(
                error.response?.data?.message || error.message
            );
        }
    };

    const downloadFile = async (url, filename) => {
        try {
            const response = await axios.get(url, {
                headers: authHeaders(),
                responseType: 'blob'
            });
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'document';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Ошибка скачивания'
            );
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 70
        },
        {
            title: 'Документ',
            dataIndex: 'title',
            render: (title, row) => (
                <div>
                    <div>{title}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.original_name}
                    </Text>
                </div>
            )
        },
        {
            title: 'Подписант',
            render: (_, row) => (
                <div>
                    <div>{row.signer_fio || '—'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {[row.signer_phone, row.signer_snils]
                            .filter(Boolean)
                            .join(' · ') || 'нет контакта'}
                    </Text>
                </div>
            )
        },
        {
            title: 'Тип',
            dataIndex: 'sign_type',
            width: 90
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 120,
            render: v => (
                <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>
            )
        },
        {
            title: 'Создан',
            dataIndex: 'created_at',
            width: 160,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        },
        {
            title: 'Действия',
            width: 320,
            render: (_, row) => (
                <Space wrap size="small">
                    {['draft', 'error', 'rejected', 'expired'].includes(
                        row.status
                    ) && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={() => sendDocument(row.id)}
                        >
                            В Госключ
                        </Button>
                    )}
                    {row.status === 'pending' && (
                        <Button
                            size="small"
                            onClick={async () => {
                                try {
                                    await axios.post(
                                        `/api/goskey/documents/${row.id}/refresh-status`,
                                        {},
                                        { headers: authHeaders() }
                                    );
                                    message.success(
                                        'Статус обновлён из API'
                                    );
                                    loadAll();
                                } catch (error) {
                                    message.error(
                                        error.response?.data
                                            ?.message ||
                                            error.message
                                    );
                                }
                            }}
                        >
                            Статус API
                        </Button>
                    )}
                    <Upload
                        showUploadList={false}
                        beforeUpload={file => {
                            uploadSigned(row.id, file);
                            return false;
                        }}
                    >
                        <Button size="small" icon={<CloudUploadOutlined />}>
                            .sig / файл
                        </Button>
                    </Upload>
                    <Button
                        size="small"
                        onClick={() =>
                            downloadFile(
                                `/api/goskey/documents/${row.id}/download`,
                                row.original_name
                            )
                        }
                    >
                        Скачать
                    </Button>
                    {row.signed_file_path && (
                        <Button
                            size="small"
                            onClick={() =>
                                downloadFile(
                                    `/api/goskey/documents/${row.id}/download-signed`,
                                    `signed_${row.id}`
                                )
                            }
                        >
                            Подписанный
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    const logColumns = [
        {
            title: 'Время',
            dataIndex: 'created_at',
            width: 170,
            render: v =>
                v ? new Date(v).toLocaleString('ru-RU') : '—'
        },
        {
            title: 'Документ',
            dataIndex: 'document_id',
            width: 100,
            render: v => v || '—'
        },
        {
            title: 'Действие',
            dataIndex: 'action',
            width: 140
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 90,
            render: v => (
                <Tag color={v === 'ok' ? 'green' : 'red'}>{v}</Tag>
            )
        },
        {
            title: 'Детали',
            dataIndex: 'details',
            render: details =>
                details
                    ? details.note ||
                      details.message ||
                      details.error ||
                      JSON.stringify(details)
                    : '—'
        }
    ];

    const stats = dashboard.stats || {};

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            <FileProtectOutlined /> Подписание через Госключ
                        </Title>
                        <Text type="secondary">
                            Загрузка документов, отправка на подпись,
                            статусы и журнал (только администраторы)
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={loadAll}
                    >
                        Обновить
                    </Button>
                </Space>

                {!dashboard.settings?.api_key_set && (
                    <Alert
                        style={{ marginBottom: 16 }}
                        type="warning"
                        showIcon
                        message="API-ключ не задан"
                        description="Откройте вкладку «Настройки API» и укажите base URL + API key. Без ключа отправка в Госключ недоступна."
                    />
                )}

                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {[
                        ['Всего', stats.total],
                        ['Черновики', stats.draft],
                        ['Ожидают', stats.pending],
                        ['Подписаны', stats.signed],
                        ['Отклонены', stats.rejected],
                        ['Ошибки', stats.error]
                    ].map(([label, value]) => (
                        <Col span={4} key={label}>
                            <Card size="small">
                                <Text type="secondary">{label}</Text>
                                <Title level={3} style={{ margin: 0 }}>
                                    {value || 0}
                                </Title>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Tabs
                    items={[
                        {
                            key: 'docs',
                            label: 'Документы',
                            children: (
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Card
                                            title="Новый документ на подпись"
                                            size="small"
                                        >
                                            <Form
                                                form={createForm}
                                                layout="vertical"
                                                onFinish={createDocument}
                                                initialValues={{
                                                    sign_type: 'УНЭП'
                                                }}
                                            >
                                                <Form.Item
                                                    name="title"
                                                    label="Название"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                'Укажите название'
                                                        }
                                                    ]}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="description"
                                                    label="Описание"
                                                >
                                                    <TextArea rows={2} />
                                                </Form.Item>
                                                <Form.Item
                                                    name="signer_fio"
                                                    label="ФИО подписанта"
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="signer_phone"
                                                    label="Телефон"
                                                >
                                                    <Input placeholder="+7..." />
                                                </Form.Item>
                                                <Form.Item
                                                    name="signer_snils"
                                                    label="СНИЛС"
                                                >
                                                    <Input placeholder="XXX-XXX-XXX XX" />
                                                </Form.Item>
                                                <Form.Item
                                                    name="signer_email"
                                                    label="Email"
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="sign_type"
                                                    label="Тип подписи"
                                                >
                                                    <Select
                                                        options={[
                                                            {
                                                                value: 'УНЭП',
                                                                label: 'УНЭП'
                                                            },
                                                            {
                                                                value: 'УКЭП',
                                                                label: 'УКЭП'
                                                            }
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    name="expires_at"
                                                    label="Срок подписания"
                                                >
                                                    <DatePicker
                                                        showTime
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Файл"
                                                    required
                                                >
                                                    <Upload
                                                        beforeUpload={file => {
                                                            setFileList([
                                                                {
                                                                    uid: '1',
                                                                    name: file.name,
                                                                    status: 'done',
                                                                    originFileObj:
                                                                        file
                                                                }
                                                            ]);
                                                            return false;
                                                        }}
                                                        onRemove={() =>
                                                            setFileList([])
                                                        }
                                                        fileList={fileList}
                                                        maxCount={1}
                                                    >
                                                        <Button>
                                                            Выбрать файл
                                                        </Button>
                                                    </Upload>
                                                </Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    block
                                                >
                                                    Создать
                                                </Button>
                                            </Form>
                                        </Card>
                                    </Col>
                                    <Col span={16}>
                                        <Card
                                            title="Реестр документов"
                                            size="small"
                                            extra={
                                                <Select
                                                    allowClear
                                                    placeholder="Статус"
                                                    style={{ width: 160 }}
                                                    value={filterStatus}
                                                    onChange={setFilterStatus}
                                                    options={[
                                                        'draft',
                                                        'pending',
                                                        'signed',
                                                        'rejected',
                                                        'expired',
                                                        'error',
                                                        'cancelled'
                                                    ].map(v => ({
                                                        value: v,
                                                        label: v
                                                    }))}
                                                />
                                            }
                                        >
                                            <Table
                                                size="small"
                                                rowKey="id"
                                                loading={loading}
                                                columns={columns}
                                                dataSource={documents}
                                                pagination={{
                                                    pageSize: 10
                                                }}
                                                scroll={{ x: 1100 }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            )
                        },
                        {
                            key: 'settings',
                            label: 'Настройки API',
                            children: (
                                <Card size="small" style={{ maxWidth: 720 }}>
                                    <Form
                                        form={settingsForm}
                                        layout="vertical"
                                        onFinish={saveSettings}
                                        initialValues={{ mode: 'api' }}
                                    >
                                        <Form.Item
                                            name="mode"
                                            label="Режим"
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="api_base_url"
                                            label="API base URL"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        'Укажите URL API'
                                                }
                                            ]}
                                        >
                                            <Input placeholder="https://your-goskey-api.example/api/v1" />
                                        </Form.Item>
                                        <Form.Item
                                            name="api_key"
                                            label="API key"
                                            extra={
                                                dashboard.settings
                                                    ?.api_key_set
                                                    ? `Сейчас сохранён: ${dashboard.settings.api_key}`
                                                    : 'Ключ ещё не сохранён'
                                            }
                                        >
                                            <Input.Password placeholder="Вставьте API-ключ" />
                                        </Form.Item>
                                        <Form.Item
                                            name="org_name"
                                            label="Организация"
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="org_ogrn"
                                            label="ОГРН"
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="callback_url"
                                            label="Callback URL (webhook)"
                                            extra="Укажите провайдеру: https://ваш-домен/api/goskey/webhook"
                                        >
                                            <Input placeholder="https://crm.smartcoms.ru/api/goskey/webhook" />
                                        </Form.Item>
                                        <Form.Item
                                            name="webhook_secret"
                                            label="Webhook secret"
                                            extra={
                                                dashboard.settings
                                                    ?.webhook_secret_set
                                                    ? `Сейчас сохранён: ${dashboard.settings.webhook_secret}`
                                                    : 'Опционально: секрет для проверки callback'
                                            }
                                        >
                                            <Input.Password />
                                        </Form.Item>
                                        <Form.Item
                                            name="default_sign_type"
                                            label="Тип подписи по умолчанию"
                                        >
                                            <Select
                                                options={[
                                                    {
                                                        value: 'УНЭП',
                                                        label: 'УНЭП'
                                                    },
                                                    {
                                                        value: 'УКЭП',
                                                        label: 'УКЭП'
                                                    }
                                                ]}
                                            />
                                        </Form.Item>
                                        <Space>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                            >
                                                Сохранить
                                            </Button>
                                            <Button
                                                onClick={async () => {
                                                    try {
                                                        const {
                                                            data
                                                        } =
                                                            await axios.post(
                                                                '/api/goskey/settings/test',
                                                                {},
                                                                {
                                                                    headers:
                                                                        authHeaders()
                                                                }
                                                            );
                                                        message.success(
                                                            data.data
                                                                ?.warning ||
                                                                'Соединение с API успешно'
                                                        );
                                                    } catch (error) {
                                                        message.error(
                                                            error
                                                                .response
                                                                ?.data
                                                                ?.message ||
                                                                error.message
                                                        );
                                                    }
                                                }}
                                            >
                                                Проверить API
                                            </Button>
                                        </Space>
                                    </Form>
                                </Card>
                            )
                        },
                        {
                            key: 'logs',
                            label: 'Журнал',
                            children: (
                                <Card size="small">
                                    <Table
                                        size="small"
                                        rowKey="id"
                                        columns={logColumns}
                                        dataSource={logs}
                                        pagination={{ pageSize: 20 }}
                                    />
                                </Card>
                            )
                        }
                    ]}
                />
            </div>
        </Layout>
    );
};

export default GoskeySignPage;
